/**
 * @module project/components/entry_details/entry_details_comments
 */
import React, { PropTypes, Component } from 'react';
import idx from 'idx';
import moment from 'moment';
import { v4 } from 'node-uuid';
import classnames from 'classnames';
import EntryCommentForm from 'project/containers/entry_comment_form';
import RevisionDisclosures from 'entries/containers/revision_disclosures';
import Avatar from 'common/components/avatar';
import CsAvatar from 'assets/images/crowdspring_avatar.png';
import Icon from 'common/components/svg_icon';
import StarRating from 'common/components/star_rating';
import NegativeFeedbackResponses from 'entries/containers/negative_feedback_responses';

/**
 * Entry details comments section component
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class EntryDetailsComments extends Component {
  constructor(props) {
    super(props);

    this.state = {
      comments: {},
    };

    this.toggleNestedComments = this.toggleNestedComments.bind(this);
    this.onReply = this.onReply.bind(this);
    this.handleCallback = this.handleCallback.bind(this);
  }

  handleCallback(focus) {
    this.focusCommentForm = focus;
  }

  onReply(e, comment) {
    e.stopPropagation();
    this.props.onReplyButtonClicked(comment.id);
    this.focusCommentForm();
  }

  toggleNestedComments(commentId) {
    const comments = { ...this.state.comments };

    comments[commentId] = !comments[commentId];

    if (!comments[commentId]) {
      this.props.onAddReply();
    }

    this.setState({
      comments,
    });
  }

  renderScoreContent(comment) {
    const { entry, project } = this.props;

    return (
      <div className="feedback">
        <div className="sub-title">Rated:</div>
        <StarRating readonly={true} initialRate={comment.score || 0} />
      </div>
    );
  }

  renderFeedbackContent(comment) {
    const { entry, project } = this.props;

    return (
      <div className="feedback">
        <div className="sub-title">Provided feedback:</div>
        <StarRating readonly={true} initialRate={comment.score || 0} />
        <NegativeFeedbackResponses feedback={comment.feedback}
          subcatId={idx(project, _ => _.sub_category)} />

        { !!comment.feedback.feedback &&
        <div>
          <div className="sub-title">Comments:</div>
          <div>{ comment.feedback.feedback }</div>
        </div> }
      </div>
    );
  }

  renderCommentIndicator(comment, uncollapsed) {
    const commentsLength = comment.replies.length;
    let commentsText = 'Close';

    if (!uncollapsed) {
      commentsText = `${commentsLength} additional comment`;
      commentsText += comment.replies.length !== 1 ? 's' : '';
    }

    if (commentsLength) {
      return (
        <a
          onClick={() => { this.toggleNestedComments(comment.id); }}
          key={v4()}
          className={classnames('collapse-button', {
            uncollapsed,
          })}
        >
          {commentsText}
        </a>
      );
    }

    return null;
  }

  renderReplies(comment) {
    return (
      <div className="row replies">
        <div className="small-16 columns">
          {comment.replies.map(reply => {
            const isAuthor = reply.author.id === this.props.user.id;
            const isAdmin = reply.author.is_staff;

            return (
              <div className="comment in-place" key={v4()}>
                <div className="row">
                  <div className="small-16 columns">
                    <div className="timestamp">{moment(reply.created_at).fromNow()}</div>
                  </div>
                </div>
                <div className="row comment-wrapper">
                  <div className={"columns medium-4 small-3 small-order-" + (isAuthor? "2":"1")}>
                    <Avatar user={isAdmin? { avatar: CsAvatar } : reply.author}
                      extraClasses="avatar-round avatar-md"/>
                  </div>
                  <div className={"columns medium-12 small-13 small-order-" + (isAuthor? "1":"2")}>
                    <div className="comment-body">
                      <h5 className="comment-author">
                        { isAdmin? 'crowdspring' : `${reply.author.username || ''}` }
                      </h5>
                      <div className="comment-body-content">
                        { reply.body }
                      </div>
                    </div>
                  </div>
                </div>
              </div>)
          })}
        </div>
      </div>
    );
  }

  isInPlace(comment) {
    return comment.percent_x || comment.percent_y;
  }

  renderComments() {
    const {
      hoveredComment, commentReply, user, onCommentHover, comments, revision, entry, permissions
    } = this.props;
    let commentList = (comments || []);

    /* add revision description as the first comment */
    const firstAuthorComment = commentList.find(c => c.author.id === entry.author);
    if (revision.description) {
      commentList = [{
        id: "description",
        created_at: revision.created_at,
        body: revision.description,
        /* use full author info if there's already another comment from her, otherwise add just
           basic info */
        author: firstAuthorComment? firstAuthorComment.author : {
          id: entry.author,
          username: entry.author_username
        }
      }].concat(commentList);
    }

    return commentList.map((comment, i) => {
      let commentUncollapsed = false;
      let inPlaceComment = false;
      let inPlaceIdx = 0;

      if (this.state.comments[comment.id]) {
        commentUncollapsed = this.state.comments[comment.id];
      }

      if (this.isInPlace(comment)) {
        inPlaceComment = true;
        /* get position among in-place comments */
        inPlaceIdx = comments.filter(this.isInPlace).findIndex(c => c.id == comment.id) + 1;
      }

      const isAuthor = comment.author.id === user.id;
      const isAdmin = comment.author.is_staff;

      const replyClasses = isAuthor ? '' : 'text-right'

      return (
        <li
          className={classnames('collapse comment', {
            'is-author': isAuthor,
            'in-place': inPlaceComment,
            'active': comment.id === hoveredComment || comment.id === commentReply,
            'not-active': hoveredComment && comment.id !== hoveredComment
          })}
          key={i}
          onMouseEnter={(e) => { onCommentHover(e, comment); }}
          onMouseLeave={(e) => { onCommentHover() }}
        >
          <div className="row">
            <div className="small-16 columns">
              <div className="timestamp">
                { moment(comment.created_at).fromNow() }
              </div>
            </div>
          </div>

          <div className="row">
            <div className={"columns medium-4 xlarge-3 small-3 small-order-" + (isAuthor? "2":"1")}>
              <Avatar user={isAdmin? { avatar: CsAvatar } : comment.author}
                extraClasses="avatar-round avatar-md"/>
            </div>

            <div className={"columns medium-12 xlarge-13 small-13 small-order-" + (isAuthor? "1":"2")}>
              <div id={`comment-${comment.id}`} className={classnames("comment-body", {
                internal: comment.internal
              })}>
                {!isAuthor &&
                <h5 className="comment-author">
                  { isAdmin? 'crowdspring' : `${comment.author.username || ''}` }
                </h5> }

                <div className="comment-body-content">
                  {comment.body}
                </div>

                { !!comment.feedback && this.renderFeedbackContent(comment) }

                { !!comment.score && !comment.feedback && this.renderScoreContent(comment) }

                { inPlaceComment &&
                  <div className="badge badge-primary badge-sm">{ inPlaceIdx }</div>
                }

                { inPlaceComment &&
                  this.renderCommentIndicator(comment, commentUncollapsed)
                }
              </div>
            </div>
          </div>

          { commentUncollapsed && this.renderReplies(comment)}

          { inPlaceComment && !comment.internal && !permissions.canSendInternalCommentOnly &&
            <div className="row">
              <div className={classnames("small-16 small-order-3 columns", replyClasses)}>
                <button
                  type="button"
                  id={`${comment.id}-replyBtn`}
                  className="btn btn-primary btn-sm btn-reply"
                  onClick={(e) => this.onReply(e, comment)}
                >
                  Reply
                </button>
              </div>
            </div>
          }
        </li>
      );
    })

  }

  render() {
    const { entry, revision, onCloseComments, commentReply, onAddReply, permissions } = this.props;

    return (
      <div className="entry-comments-container">
        <div className="list-title">
          <div className="button-wrapper columns small-16 pull-right show-for-small-only">
            <button type="button" onClick={onCloseComments} className="btn btn-icon-only btn-close">
              <Icon className="svg-icon" icon="close"/>
            </button>
          </div>
          <RevisionDisclosures revision={revision} entry={entry}
            canViewAssetPrice={permissions.canViewAssetPrice} extraClasses="hide-for-small-only" />
        </div>

        <RevisionDisclosures revision={revision} entry={entry}
          canViewAssetPrice={permissions.canViewAssetPrice} extraClasses="show-for-small-only" />

        <div id="comments_scroller" className="comments-scroller">
          <ul onClick={this.props.onAddReply} className="comments-list">
            { this.renderComments() }
          </ul>
        </div>

        <div className={classnames("comment-box", {"reply" : commentReply})}>
          <EntryCommentForm
            entry={entry}
            revision={revision}
            onAddReply={onAddReply}
            commentReply={commentReply}
            methodCallback={this.handleCallback}
            canSendInternal={permissions.canSendInternalComment && !commentReply}
            canSendInternalOnly={permissions.canSendInternalCommentOnly}
            isClient={permissions.isClient}
            isCollaborator={permissions.isCollaborator}
            hasCollaborators={permissions.hasCollaborators}
          />
        </div>
      </div>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {object} user - logged in user
 * @property {object} permissions - object with user privileges
 * @property {object} entry - entry data object
 * @property {object} revision - revision data object
 * @property {array} comments - array of revision comments
 * @property {number} hoveredComment - id of hovered in-place comment
 * @property {number} commentReply - holds id of comment user is replying to and controlls
 *                                   if reply mode is on
 * @property {func} onCommentHover - function called when in-place comment is hovered
 * @property {func} onCloseComments - function called when comments modal is closed
 * @property {func} onAddReply - function called when comment reply is added
 * @property {func} onReplyButtonClicked - function called when reply button is clicked
 */
EntryDetailsComments.propTypes = {
  user: PropTypes.object,
  permissions: PropTypes.object,
  entry: PropTypes.object,
  revision: PropTypes.object,
  comments: PropTypes.array,
  hoveredComment: PropTypes.number,
  commentReply: PropTypes.number,
  onCommentHover: PropTypes.func,
  onCloseComments: PropTypes.func,
  onAddReply: PropTypes.func,
  onReplyButtonClicked: PropTypes.func,
};



// WEBPACK FOOTER //
// ./project/components/entry_details/entry_details_comments.jsx